import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import problemStatementService from '../../../services/problemStatementService';

const ProblemStatementDetailsModal = ({ isOpen, onClose, problemId }) => {
  const [problem, setProblem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && problemId) {
      fetchProblemDetails();
    }
  }, [isOpen, problemId]);

  const fetchProblemDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await problemStatementService?.getProblemStatementById(problemId);
      setProblem(data);
    } catch (err) {
      console.error('Error fetching problem details:', err);
      setError(err?.message || 'Failed to load problem details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const getCategoryLabel = (problem) => {
    if (problem?.category === 'other' && problem?.customCategory) {
      return problem?.customCategory;
    }
    const labels = {
      'transportation': 'Transportation',
      'energy': 'Energy',
      'water': 'Water',
      'waste': 'Waste',
      'housing': 'Housing',
      'safety': 'Safety',
      'governance': 'Governance',
      'health': 'Healthcare',
      'education': 'Education',
      'economy': 'Economy',
      'other': 'Other'
    };
    return labels?.[problem?.category] || problem?.category;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-border bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="FileText" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-gray-900">Problem Statement Details</h2>
              <p className="text-xs sm:text-sm text-gray-600">Complete submission summary</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="X" size={20} className="text-muted-foreground" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="flex items-center space-x-3">
                <Icon name="Loader" size={24} className="text-primary animate-spin" />
                <span className="text-sm text-muted-foreground">Loading details...</span>
              </div>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
              <Icon name="AlertCircle" size={20} className="text-red-600 mt-0.5" />
              <div>
                <h4 className="text-sm font-semibold text-red-900">Error Loading Details</h4>
                <p className="text-xs text-red-800 mt-1">{error}</p>
              </div>
            </div>
          ) : problem ? (
            <div className="space-y-6">
              {/* Overview Section */}
              <div>
                <h3 className="text-base font-semibold text-foreground mb-3 flex items-center">
                  <Icon name="Info" size={18} className="mr-2 text-primary" />
                  Overview
                </h3>
                <div className="bg-muted/30 rounded-lg p-4 space-y-3">
                  <div>
                    <span className="text-xs text-muted-foreground">Title</span>
                    <p className="text-sm font-medium text-foreground">{problem?.title}</p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <span className="text-xs text-muted-foreground">Category</span>
                      <p className="text-sm font-medium text-foreground">{getCategoryLabel(problem)}</p>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground">Priority</span>
                      <p className="text-sm font-medium text-foreground capitalize">{problem?.priority}</p>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground">Complexity</span>
                      <p className="text-sm font-medium text-foreground capitalize">{problem?.complexity}</p>
                    </div>
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground">Description</span>
                    <p className="text-sm text-foreground mt-1">{problem?.description}</p>
                  </div>
                  {problem?.background && (
                    <div>
                      <span className="text-xs text-muted-foreground">Background</span>
                      <p className="text-sm text-foreground mt-1">{problem?.background}</p>
                    </div>
                  )}
                  {problem?.affectedPopulation && (
                    <div>
                      <span className="text-xs text-muted-foreground">Affected Population</span>
                      <p className="text-sm text-foreground mt-1">{problem?.affectedPopulation}</p>
                    </div>
                  )}
                  {problem?.currentImpact && (
                    <div>
                      <span className="text-xs text-muted-foreground">Current Impact</span>
                      <p className="text-sm text-foreground mt-1">{problem?.currentImpact}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Requirements Section */}
              {(problem?.objectives?.length > 0 || problem?.constraints?.length > 0 || problem?.expectedDeliverables?.length > 0 || problem?.successMetrics?.length > 0) && (
                <div>
                  <h3 className="text-base font-semibold text-foreground mb-3 flex items-center">
                    <Icon name="Target" size={18} className="mr-2 text-primary" />
                    Requirements
                  </h3>
                  <div className="bg-muted/30 rounded-lg p-4 space-y-3">
                    {problem?.objectives?.length > 0 && (
                      <div>
                        <span className="text-xs text-muted-foreground">Objectives</span>
                        <ul className="list-disc list-inside text-sm text-foreground mt-1 space-y-1">
                          {problem?.objectives?.map((obj, idx) => (
                            <li key={idx}>{obj}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {problem?.constraints?.length > 0 && (
                      <div>
                        <span className="text-xs text-muted-foreground">Constraints</span>
                        <ul className="list-disc list-inside text-sm text-foreground mt-1 space-y-1">
                          {problem?.constraints?.map((con, idx) => (
                            <li key={idx}>{con}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {problem?.expectedDeliverables?.length > 0 && (
                      <div>
                        <span className="text-xs text-muted-foreground">Expected Deliverables</span>
                        <ul className="list-disc list-inside text-sm text-foreground mt-1 space-y-1">
                          {problem?.expectedDeliverables?.map((del, idx) => (
                            <li key={idx}>{del}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {problem?.successMetrics?.length > 0 && (
                      <div>
                        <span className="text-xs text-muted-foreground">Success Metrics</span>
                        <ul className="list-disc list-inside text-sm text-foreground mt-1 space-y-1">
                          {problem?.successMetrics?.map((metric, idx) => (
                            <li key={idx}>{metric}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {problem?.budgetRange && (
                      <div>
                        <span className="text-xs text-muted-foreground">Budget Range</span>
                        <p className="text-sm text-foreground mt-1">{problem?.budgetRange}</p>
                      </div>
                    )}
                    {problem?.resourceAvailability && (
                      <div>
                        <span className="text-xs text-muted-foreground">Resource Availability</span>
                        <p className="text-sm text-foreground mt-1">{problem?.resourceAvailability}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Stakeholders Section */}
              {(problem?.primaryContact || problem?.stakeholders?.length > 0) && (
                <div>
                  <h3 className="text-base font-semibold text-foreground mb-3 flex items-center">
                    <Icon name="Users" size={18} className="mr-2 text-primary" />
                    Stakeholders
                  </h3>
                  <div className="bg-muted/30 rounded-lg p-4 space-y-3">
                    {problem?.primaryContact && (
                      <div>
                        <span className="text-xs text-muted-foreground">Primary Contact</span>
                        <div className="text-sm text-foreground mt-1">
                          <p className="font-medium">{problem?.primaryContact?.name}</p>
                          {problem?.primaryContact?.title && <p>{problem?.primaryContact?.title}</p>}
                          {problem?.primaryContact?.email && <p>{problem?.primaryContact?.email}</p>}
                          {problem?.primaryContact?.phone && <p>{problem?.primaryContact?.phone}</p>}
                        </div>
                      </div>
                    )}
                    {problem?.stakeholders?.length > 0 && (
                      <div>
                        <span className="text-xs text-muted-foreground">Additional Stakeholders</span>
                        <div className="space-y-2 mt-1">
                          {problem?.stakeholders?.map((stakeholder, idx) => (
                            <div key={idx} className="text-sm text-foreground">
                              <p className="font-medium">{stakeholder?.name}</p>
                              {stakeholder?.role && <p className="text-xs">{stakeholder?.role}</p>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {problem?.communicationPreferences?.length > 0 && (
                      <div>
                        <span className="text-xs text-muted-foreground">Communication Preferences</span>
                        <p className="text-sm text-foreground mt-1">{problem?.communicationPreferences?.join(', ')}</p>
                      </div>
                    )}
                    {problem?.meetingFrequency && (
                      <div>
                        <span className="text-xs text-muted-foreground">Meeting Frequency</span>
                        <p className="text-sm text-foreground mt-1">{problem?.meetingFrequency}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Timeline Section */}
              {(problem?.projectDuration || problem?.projectStartDate || problem?.milestones?.length > 0) && (
                <div>
                  <h3 className="text-base font-semibold text-foreground mb-3 flex items-center">
                    <Icon name="Calendar" size={18} className="mr-2 text-primary" />
                    Timeline
                  </h3>
                  <div className="bg-muted/30 rounded-lg p-4 space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {problem?.projectDuration && (
                        <div>
                          <span className="text-xs text-muted-foreground">Project Duration</span>
                          <p className="text-sm font-medium text-foreground">{problem?.projectDuration}</p>
                        </div>
                      )}
                      {problem?.urgencyLevel && (
                        <div>
                          <span className="text-xs text-muted-foreground">Urgency Level</span>
                          <p className="text-sm font-medium text-foreground capitalize">{problem?.urgencyLevel}</p>
                        </div>
                      )}
                      {problem?.projectStartDate && (
                        <div>
                          <span className="text-xs text-muted-foreground">Start Date</span>
                          <p className="text-sm font-medium text-foreground">{formatDate(problem?.projectStartDate)}</p>
                        </div>
                      )}
                      {problem?.projectEndDate && (
                        <div>
                          <span className="text-xs text-muted-foreground">End Date</span>
                          <p className="text-sm font-medium text-foreground">{formatDate(problem?.projectEndDate)}</p>
                        </div>
                      )}
                    </div>
                    {problem?.milestones?.length > 0 && (
                      <div>
                        <span className="text-xs text-muted-foreground">Milestones</span>
                        <div className="space-y-2 mt-1">
                          {problem?.milestones?.map((milestone, idx) => (
                            <div key={idx} className="text-sm text-foreground flex items-start space-x-2">
                              <Icon name="CheckCircle" size={14} className="text-primary mt-0.5" />
                              <div>
                                <p className="font-medium">{milestone?.name}</p>
                                {milestone?.date && <p className="text-xs text-muted-foreground">{formatDate(milestone?.date)}</p>}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {problem?.seasonalConsiderations && (
                      <div>
                        <span className="text-xs text-muted-foreground">Seasonal Considerations</span>
                        <p className="text-sm text-foreground mt-1">{problem?.seasonalConsiderations}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Attachments Section */}
              {problem?.attachments?.length > 0 && (
                <div>
                  <h3 className="text-base font-semibold text-foreground mb-3 flex items-center">
                    <Icon name="Paperclip" size={18} className="mr-2 text-primary" />
                    Attachments
                  </h3>
                  <div className="bg-muted/30 rounded-lg p-4">
                    <div className="space-y-2">
                      {problem?.attachments?.map((attachment, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-card rounded border border-border">
                          <div className="flex items-center space-x-2">
                            <Icon name="File" size={16} className="text-primary" />
                            <span className="text-sm text-foreground">{attachment?.name}</span>
                          </div>
                          {attachment?.url && (
                            <a href={attachment?.url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline">
                              View
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Status Section */}
              <div>
                <h3 className="text-base font-semibold text-foreground mb-3 flex items-center">
                  <Icon name="Activity" size={18} className="mr-2 text-primary" />
                  Status
                </h3>
                <div className="bg-muted/30 rounded-lg p-4 space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <span className="text-xs text-muted-foreground">Submission Status</span>
                      <p className="text-sm font-medium text-foreground capitalize">{problem?.status}</p>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground">Submitted On</span>
                      <p className="text-sm font-medium text-foreground">{formatDate(problem?.createdAt)}</p>
                    </div>
                    {problem?.assignedTeam && (
                      <div>
                        <span className="text-xs text-muted-foreground">Assigned Team</span>
                        <p className="text-sm font-medium text-foreground">{problem?.assignedTeam?.team_name}</p>
                      </div>
                    )}
                    {problem?.currentPhase && (
                      <div>
                        <span className="text-xs text-muted-foreground">Current Phase</span>
                        <p className="text-sm font-medium text-foreground">{problem?.currentPhase}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div className="p-4 sm:p-6 border-t border-border bg-muted/20 flex justify-end">
          <Button
            variant="default"
            onClick={onClose}
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProblemStatementDetailsModal;